import { KEYBOARD_MIN_INSET_PX, KEYBOARD_SETTLE_MAX_MS, KEYBOARD_SETTLE_MIN_MS } from './config'

/**
 * Замер нижней кромки окна в один момент времени: всё, что нужно, чтобы понять, на сколько
 * поднимать закреплённые снизу элементы.
 */
export type ViewportSample = {
  /**
   * Высота окна вебвью (`documentElement.clientHeight`). От неё живёт `position: fixed`
   * и от неё же считается клавиатура.
   */
  layoutHeight: number
  /**
   * Ширина окна вебвью. В расчёт подъёма не входит — она только отличает поворот экрана
   * от появления клавиатуры: высота меняется и там, и там, ширина — только при повороте.
   */
  layoutWidth: number
  /**
   * Видимая высота по данным Telegram (`viewportStableHeight`); 0 — источника нет
   * (браузер или клиент, который её не отдаёт).
   */
  reportedStableHeight: number
  /** Высота видимой полосы по `visualViewport`: то, что осталось от окна после клавиатуры. */
  visualHeight: number
  /** На сколько видимая полоса съехала вниз внутри окна: iOS двигает её к полю ввода. */
  visualOffsetTop: number
  /** В фокусе текстовое поле. Экранной клавиатуры без сфокусированного поля не бывает. */
  isTextInputFocused: boolean
}

export type BottomLiftState = {
  /** Мёртвая полоса окна: часть окна ниже видимой, о которой знает только Telegram. */
  band: number
  /** Сдвиг видимой полосы вниз внутри окна; 0, если клавиатуры нет. */
  offsetTop: number
  /**
   * Сколько окна скрыто снизу ПРЯМО СЕЙЧАС — по замерам этого самого кадра.
   * Это физический потолок подъёма: выше кнопке подниматься не с чего.
   */
  hidden: number
  /** Подъём, который можно публиковать прямо сейчас. Всегда ≤ `hidden`. */
  lift: number
  /**
   * Где стоит нижняя кромка шторки, считая от низа окна. Отличается от `lift` тем, что замер
   * клавиатуры входит сюда СЫРЫМ, замером текущего кадра (правило 5).
   */
  sheetLift: number
  /**
   * Видимое место над этой кромкой: по нему шторка меряет свою высоту. Считается тем же
   * кадром, что и `sheetLift`, — коробка шторки обязана ехать одним движением (правило 5).
   */
  sheetRoom: number
  /** Замер ещё не устоялся — нужен новый на следующем кадре. */
  isPending: boolean
  /** Публикуемая величина только что сменилась. */
  hasChanged: boolean
}

export type BottomLift = (sample: ViewportSample, now: number) => BottomLiftState

/**
 * Сколько окна снизу занимает клавиатура по данным `visualViewport`; 0 — её нет.
 *
 * Клавиатуры без сфокусированного поля не бывает, и проверка нужна не для красоты: iOS шлёт
 * `resize` visualViewport'а ПОСЛЕ того, как клавиатура доехала вниз, и без неё закреплённая
 * кнопка ещё секунду висела на месте закрытой клавиатуры, а только потом падала.
 *
 * `offsetTop` вычитается, потому что iOS подтягивает видимую полосу к полю: окно стоит на
 * месте, а полоса съезжает вниз, и до низа окна от неё остаётся меньше.
 */
export function measureKeyboard(sample: ViewportSample): number {
  const inset = sample.layoutHeight - sample.visualHeight - sample.visualOffsetTop

  // Клавиатуры нет — значит нет и сдвига полосы: тот же сдвиг даёт обычный pinch-zoom,
  // и раскладка не должна на него дёргаться.
  if (!sample.isTextInputFocused || inset < KEYBOARD_MIN_INSET_PX) return 0

  return Math.round(inset)
}

/**
 * Мёртвая полоса окна: сколько его нижней части не видно.
 *
 * Источника два и берётся МЕНЬШАЯ высота: `viewportStableHeight` от Telegram знает про
 * видимую полосу (в вебвью на iOS окно выкладывается больше неё), а `clientHeight` — про
 * клиентов (обычно Android), которые вебвью реально ужимают и о которых Telegram не сообщает.
 */
export function measureBand(sample: ViewportSample): number {
  const layoutHeight = Math.round(sample.layoutHeight)
  const reported = Math.round(sample.reportedStableHeight)

  if (reported <= 0) return 0

  return Math.max(0, layoutHeight - Math.min(layoutHeight, reported))
}

/**
 * Единственный счётчик подъёма: на сколько поднимается всё, что закреплено у нижнего края окна
 * (футер экрана, меню-остров) и где стоит коробка шторки. Величины уезжают в
 * `--layout-bottom-lift`, `--layout-sheet-lift` и `--layout-sheet-room`; больше нигде ни одна
 * из них не пересобирается.
 *
 * Пять правил, каждое куплено прогоном на живом iPhone (docs/telegram.md §5.3, §5.4):
 *
 * 1. **Мёртвая полоса не растёт от клавиатуры.** Пока в поле стоит курсор, полоса не может
 *    стать больше своего последнего замера без клавиатуры. Telegram на iOS в этот момент
 *    рассказывает про клавиатуру своим способом — присылает `viewportChanged` с ужатой
 *    «стабильной» высотой, — и разница окна с ней становится размером с клавиатуру. Про
 *    клавиатуру у нас есть точный источник (`visualViewport`), и он знает про сдвиг полосы
 *    к полю, а число Telegram — нет: оно завышено ровно на этот сдвиг. Ограничение работает
 *    в одну сторону, только на уменьшение, — поэтому ошибиться оно может лишь тем, что кнопка
 *    встанет НИЖЕ, за клавиатурой, а не улетит вверх.
 * 2. **Наружу уходит только устоявшаяся величина.** Клавиатура на iOS приезжает несколькими
 *    числами за время своей анимации, и промежуточные завышены. Кто двигается на первом же —
 *    перелетает вверх и доводится вторым движением. Ждать нечего: пока клавиатура едет, кнопка
 *    всё равно закрыта ею. Срок отсчитывается от ПОСЛЕДНЕГО изменения: будильник от первого
 *    замера срабатывает вслепую посреди уточнений, и кнопка едет дважды.
 * 3. **Инвариант: подъём никогда не больше того, что скрыто снизу прямо сейчас.**
 *    `lift ≤ max(мёртвая полоса, клавиатура)` по замерам ТЕКУЩЕГО кадра, без всякой памяти.
 *    Любая будущая ошибка в расчётах даст неточность, а не улёт кнопки в потолок: ровно так
 *    закончилась попытка удержать высоты окна на «свободном» замере — подъём получался равным
 *    клавиатуре плюс полосе, которую Telegram уже забрал, и кнопка уезжала под самый верх на
 *    всё время фокуса. Ограничение действует только вниз, то есть в сторону «кнопка ещё
 *    спрятана за клавиатурой», а этого на экране не видно.
 * 4. **Шторка живёт не в окне, а в видимой полосе.** Правила 2 и 3 писались про футер — тонкую
 *    полосу, которую едущая клавиатура и так закрывает целиком: подождать четверть секунды
 *    внизу ему ничего не стоит. Шторка занимает почти весь экран, и та же выдержка выглядит
 *    иначе: видимая полоса уже уехала вверх (iOS подтянул её к полю), а шторка всё ещё стоит
 *    у низа ОКНА — то есть ниже всего, что видно. Это и есть «шторка сначала уходит вниз»
 *    с устройства основателя. Поэтому у шторки свои две величины, и обе — по замерам ТЕКУЩЕГО
 *    кадра: `sheetLift` ставит её нижнюю кромку на нижнюю границу видимой полосы, `sheetRoom`
 *    отдаёт ей ровно ту полосу, что видна. Промежуточный замер клавиатуры тут не «неточный»:
 *    он честный на свой кадр, и вместе со сдвигом полосы того же кадра даёт коробку, которая
 *    на экране стоит на месте. Устоявшийся `lift` при этом остаётся нижней границей: шторка
 *    не может встать ниже футера.
 *
 * 5. **Коробка шторки меняется парой.** `sheetLift` и `sheetRoom` считаются из одних и тех же
 *    чисел одного кадра, поэтому `bottom` и высота трогаются вместе и едут одним переходом.
 *    Пока это было не так (высота от сырого окна, подъём — от устоявшегося), верхняя кромка
 *    прыгала вниз, а `bottom` вёз шторку вверх: два движения, к тому же разнонаправленных.
 *
 * Состояние держит замыкание, время приходит снаружи — так это проверяется тестом без таймеров
 * и кадров.
 */
export function createBottomLift(): BottomLift {
  /** Последняя мёртвая полоса, замеренная без клавиатуры; null — такого замера ещё не было. */
  let freeBand: number | null = null
  /** При какой ширине окна она замерена: после поворота экрана прошлый замер не про это окно. */
  let freeBandWidth = 0
  /** Величина, дошедшая до раскладки. */
  let committed = 0
  /** Последняя опубликованная величина — по ней считается `hasChanged`. */
  let published = 0
  /** Замер, который ждёт своей очереди; null — расхождения с `committed` нет. */
  let candidate: number | null = null
  /** Когда кандидат в последний раз изменился. */
  let changedAt = 0
  /** Когда замер впервые разошёлся с опубликованным; null — расхождения нет. */
  let pendingSince: number | null = null

  const reset = () => {
    candidate = null
    pendingSince = null
  }

  return (sample, now) => {
    const keyboard = measureKeyboard(sample)
    const measuredBand = measureBand(sample)

    // Правило 1: мёртвая полоса запоминается только без клавиатуры и при ней не растёт.
    // Поворот экрана — единственное, что отменяет запомненное: там окно другое, и держать
    // кнопку по портретной полосе значило бы прятать её за нижним краем до снятия фокуса.
    if (!sample.isTextInputFocused || sample.layoutWidth !== freeBandWidth) {
      freeBand = measuredBand
      freeBandWidth = sample.layoutWidth
    }
    const band = freeBand === null ? measuredBand : Math.min(measuredBand, freeBand)

    // Клавиатура закрывает и мёртвую полосу тоже, поэтому величины не складываются,
    // а конкурируют: берём максимум.
    const hidden = Math.max(band, keyboard)

    let isPending = false

    if (!sample.isTextInputFocused) {
      // Клавиатуры нет — ждать нечего. Закрытие мы узнаём по фокусу, а не по размеру, и это
      // единственный точный сигнал: иначе кнопка виснет на месте уехавшей клавиатуры.
      committed = hidden
      reset()
    } else if (hidden === committed) {
      reset()
    } else {
      // Правило 2: держим прежнюю величину, пока замер не перестанет меняться.
      if (candidate !== hidden) {
        candidate = hidden
        changedAt = now
        pendingSince ??= now
      }

      const isSettled = now - changedAt >= KEYBOARD_SETTLE_MIN_MS
      // Предохранитель: если размеры не успокаиваются вовсе (внешняя клавиатура, чужая
      // анимация), публикуем то, что намерили, а не держим кнопку внизу бесконечно.
      const isOverdue = now - (pendingSince ?? now) >= KEYBOARD_SETTLE_MAX_MS

      if (isSettled || isOverdue) {
        committed = hidden
        reset()
      } else {
        isPending = true
      }
    }

    // Правило 3, инвариант. Стоит последним и считается по этому же кадру.
    const lift = Math.min(committed, hidden)

    // Клавиатуры нет — значит нет и сдвига полосы: тот же сдвиг даёт обычный pinch-zoom.
    const offsetTop = keyboard > 0 ? Math.round(sample.visualOffsetTop) : 0
    const layoutHeight = Math.round(sample.layoutHeight)

    // Правило 4: нижняя кромка шторки — это нижняя граница видимой полосы, а она от низа окна
    // ровно на `keyboard` замера ЭТОГО кадра. Ниже устоявшегося подъёма шторка не опускается:
    // мёртвую полосу окна `visualViewport` не видит, про неё знает только Telegram.
    // Инвариант правила 3 держится сам собой: оба слагаемых максимума ≤ `hidden`.
    const sheetLift = Math.max(lift, keyboard)
    // Правило 5: пара к `sheetLift`, из тех же чисел того же кадра. При клавиатуре это ровно
    // `visualHeight` — то, что видно; без неё — окно за вычетом подъёма, как и было.
    // `sheetLift + sheetRoom ≤ layoutHeight` по построению: наверх шторку не вынесет.
    const sheetRoom = Math.max(0, layoutHeight - sheetLift - offsetTop)

    const hasChanged = lift !== published
    published = lift

    return {
      band,
      offsetTop,
      hidden,
      lift,
      sheetLift,
      sheetRoom,
      isPending,
      hasChanged,
    }
  }
}
